import React from "react";
import { filter as filterData } from "@common/filter";
import { resource } from "@planzone/dto";

import { useUtils } from "@common/treeToggle";
import { Context } from "../context";
import { ResourceCtx } from "../wbs/resourceCtx";
import { ProjectCtx } from "../wbs/projectCtx";
import { renderWithTreeCtx } from "../utils";
import { Props } from "./types";
import { Details } from "./details";
import styles from "./styles.scss";

function DetailsView({ workLoads, projects, filter, sort, zone }: Props) {
  const { cell } = React.useContext(Context);
  const { isOpen } = useUtils();

  const filteredWorkLoads = filterData(workLoads, filter, (workLoad) => ({
    resource: {
      ...resource,
      uuid: workLoad.uuid,
      name: workLoad.resourceName,
      tags: workLoad.resourceTags.map((name) => ({ name })),
    },
  }));

  return (
    <div className={styles.detailsView}>
      {filteredWorkLoads.map((workLoad) => {
        const isResourceOpen = isOpen(workLoad.uuid);

        return (
          <React.Fragment key={workLoad.uuid}>
            <div
              style={{
                height: cell.height,
                borderBottom: "1px solid var(--color-grey)",
              }}
            ></div>

            {isResourceOpen && (
              <ResourceCtx resource={workLoad} zone={zone} sort={sort}>
                {(projectsForResource, isLoadingResource) => {
                  if (isLoadingResource) {
                    return <div style={{ height: cell.height }}></div>;
                  }

                  const filteredProjects = filterData(
                    projectsForResource,
                    filter,
                    (project) => ({ project }),
                  );

                  return (
                    <>
                      {filteredProjects.map((project) => (
                        <ProjectCtx
                          key={project.uuid}
                          workLoad={workLoad}
                          project={project}
                          filter={filter}
                          sort={sort}
                          isMultiProject={filteredProjects.length > 1}
                          hideProjects={false}
                        >
                          {(ganttByProject, isLoadingProject) => {
                            if (isLoadingProject) {
                              return (
                                <div style={{ height: cell.height }}></div>
                              );
                            }

                            return (
                              <>
                                {renderWithTreeCtx(
                                  ganttByProject,
                                  isOpen,
                                  workLoad,
                                  (element, ctx) => {
                                    const isElementVisible = (
                                      el: typeof element,
                                    ): boolean => {
                                      if (!el.parentUuid || el.depth === 0) {
                                        return true;
                                      }
                                      if (
                                        !isOpen(`${workLoad.uuid}:${el.uuid}`)
                                      ) {
                                        return false;
                                      }
                                      const parent = ganttByProject.find(
                                        (p) => p.uuid === el.parentUuid,
                                      );
                                      if (parent) {
                                        return isElementVisible(parent);
                                      }
                                      return true;
                                    };

                                    if (!isElementVisible(element)) {
                                      return (
                                        <React.Fragment
                                          key={element.uuid}
                                        ></React.Fragment>
                                      );
                                    }

                                    return (
                                      <div
                                        key={element.uuid}
                                        style={{ height: cell.height }}
                                      >
                                        <Details
                                          element={element}
                                          projects={projects}
                                          {...ctx}
                                        />
                                      </div>
                                    );
                                  },
                                )}
                              </>
                            );
                          }}
                        </ProjectCtx>
                      ))}
                    </>
                  );
                }}
              </ResourceCtx>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export { DetailsView };
